import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, patch, post, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { PRIORITY_DOT, TYPE_COLOR, relative, dateFmt } from "@/lib/format";
import { pushRecent } from "@/components/CommandPalette";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Link2, Trash2, AlertTriangle, Pencil, History, ChevronRight } from "lucide-react";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import ApprovalBanner from "@/pages/pm/ApprovalBanner";
import { toast } from "sonner";

const TICKET_TYPES = ["Epic", "Module", "Task", "Subtask", "Bug", "Feature", "Activity"];

export default function WorkItemDetail() {
  const { id, itemId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, me } = useApp();
  const [comment, setComment] = useState("");
  const [editCommentId, setEditCommentId] = useState(null);
  const [editCommentBody, setEditCommentBody] = useState("");
  const [overrideId, setOverrideId] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const { data: item, isLoading } = useQuery({ queryKey: ["item", itemId], queryFn: () => get(`/pm/items/${itemId}`) });
  const { data: timeLogged } = useQuery({ queryKey: ["item-time-logged", itemId], queryFn: () => get(`/pm/items/${itemId}/time-logged`) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const { data: siblings = [] } = useQuery({ queryKey: ["items", id], queryFn: () => get(`/pm/projects/${id}/items`) });

  useEffect(() => { if (item) pushRecent({ title: item.title, path: `/pm/projects/${id}/items/${itemId}` }); }, [item, id, itemId]);

  const update = useMutation({
    mutationFn: (body) => patch(`/pm/items/${itemId}`, { ...body, _actor: me?.id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["item", itemId] }),
  });
  const addComment = useMutation({
    mutationFn: (body) => post(`/pm/items/${itemId}/comments`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["item", itemId] }); setComment(""); toast.success("Comment added"); },
  });
  const editComment = useMutation({
    mutationFn: ({ cid, body }) => patch(`/pm/items/${itemId}/comments/${cid}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["item", itemId] }); setEditCommentId(null); toast.success("Comment updated"); },
  });
  const deleteComment = useMutation({
    mutationFn: (cid) => del(`/pm/items/${itemId}/comments/${cid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["item", itemId] }); toast.success("Comment deleted"); },
  });
  const addDep = useMutation({
    mutationFn: (item_id) => post(`/pm/items/${itemId}/dependencies`, { item_id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["item", itemId] }); toast.success("Dependency added"); },
  });

  if (isLoading || !item) return <Skeleton className="h-96" />;

  const isPM = can("pm", "override");
  const children = siblings.filter((s) => s.parent_id === itemId);
  const parent = siblings.find((s) => s.id === item.parent_id);

  const toggleCheck = (list, cid) => {
    const updated = item[list].map((c) => c.id === cid ? { ...c, checked: !c.checked, by: me?.id } : c);
    update.mutate({ [list]: updated });
  };
  const applyOverride = () => {
    ["dor", "dod"].forEach((list) => {
      if (item[list].some((c) => c.id === overrideId)) {
        const u = item[list].map((c) => c.id === overrideId ? { ...c, checked: true, overridden: true, override_reason: overrideReason } : c);
        update.mutate({ [list]: u });
      }
    });
    setOverrideId(null); setOverrideReason(""); toast.success("Requirement overridden");
  };

  const Checklist = ({ list, title }) => (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="mb-3 font-display text-sm font-semibold">{title}</h3>
      <div className="space-y-2">
        {item[list].map((c) => (
          <div key={c.id} className="flex items-start gap-2.5">
            <button data-testid={`check-${c.id}`} onClick={() => !c.overridden && toggleCheck(list, c.id)}
              className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded", c.checked ? (c.overridden ? "bg-amber-500 text-white" : "bg-emerald-500 text-white") : "border-2 border-border")}>
              {c.checked && <Check className="h-3 w-3" />}
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-1.5 text-sm">
                {c.label}
                {c.required && <span className="h-1.5 w-1.5 rounded-full bg-red-500" title="Required" />}
                {c.overridden && <StatusBadge family="amber" label="Overridden" />}
              </div>
              {c.checked && !c.overridden && <div className="text-xs text-muted-foreground">by {umap[c.by]?.name || "—"}</div>}
              {c.overridden && <div className="text-xs text-muted-foreground">{c.override_reason}</div>}
              {c.required && !c.checked && (isPM ? (
                overrideId === c.id ? (
                  <div className="mt-1.5 flex gap-2">
                    <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Reason" className="h-7 text-xs" data-testid="override-reason" />
                    <Button size="sm" className="h-7" onClick={applyOverride} disabled={!overrideReason} data-testid="confirm-override">Confirm</Button>
                  </div>
                ) : <button onClick={() => setOverrideId(c.id)} className="text-xs text-primary hover:underline" data-testid={`override-${c.id}`}>Override</button>
              ) : <span className="text-xs text-muted-foreground">Requires PM override</span>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-2 text-xs text-muted-foreground">
        <button onClick={() => navigate(`/pm/projects/${id}/board`)} className="hover:underline">Board</button>
        {parent && <> / <button onClick={() => navigate(`/pm/projects/${id}/items/${parent.id}`)} className="hover:underline">{parent.title}</button></>}
        {" "}/ {item.title}
      </div>
      <div className="mb-6 flex items-center gap-3">
        <StatusBadge family={TYPE_COLOR[item.type]} label={item.type} />
        <h1 className="font-display text-xl font-bold tracking-tight">{item.title}</h1>
        <span className={cn("h-2.5 w-2.5 rounded-full", PRIORITY_DOT[item.priority])} title={item.priority} />
        {item.severity && <StatusBadge status={item.severity} />}
        {item.blocked && <StatusBadge family="red" label="Blocked" />}
        <button data-testid="toggle-history" onClick={() => setShowHistory((s) => !s)} className="text-muted-foreground hover:text-foreground" title="Status history">
          <History className="h-4 w-4" />
        </button>
        {can("pm", "delete") && (
          <ConfirmDelete testid="del-item" title="Delete this work item?"
            trigger={<Button variant="outline" size="icon" className="ml-auto" data-testid="del-item-btn"><Trash2 className="h-4 w-4" /></Button>}
            onConfirm={async () => { await del(`/pm/items/${itemId}?_actor=${me?.id || ""}`); toast.success("Item deleted"); navigate(`/pm/projects/${id}/board`); }} />
        )}
      </div>

      {showHistory && (
        <div className="mb-4 rounded-xl border border-border bg-card p-4" data-testid="status-history">
          <h3 className="mb-2 font-display text-sm font-semibold">Status Change History</h3>
          {(item.status_history || []).length === 0 ? <p className="text-sm text-muted-foreground">No status changes yet.</p> : (
            <div className="space-y-1.5">
              {[...item.status_history].reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{umap[h.by]?.name || "Someone"}</span>
                  <span>moved this from</span><span className="font-medium text-foreground">{h.from_column_id}</span>
                  <ChevronRight className="h-3 w-3" /><span className="font-medium text-foreground">{h.to_column_id}</span>
                  <span className="ml-auto">{relative(h.at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ApprovalBanner item={item} />

      {item.type === "Epic" && <EpicBurnup epicId={item.id} />}

      {isPM && item.type === "Task" && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-border bg-card p-4">
          <span className="text-sm font-medium">Spike</span>
          <span className="text-xs text-muted-foreground">Time-boxed research work — skips DoD and approval gates.</span>
          <button data-testid="toggle-spike" onClick={() => update.mutate({ is_spike: !item.is_spike })}
            className={cn("ml-auto rounded-md px-2 py-1 text-xs font-medium", item.is_spike ? "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" : "bg-muted text-muted-foreground")}>
            {item.is_spike ? "Unmark Spike" : "Mark as Spike"}
          </button>
        </div>
      )}

      {isPM && (
        <div className="mb-4 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className={cn("h-4 w-4", item.blocked ? "text-red-500" : "text-muted-foreground")} />
            <span className="text-sm font-medium">Blocked</span>
            <button data-testid="toggle-blocked" onClick={() => item.blocked ? update.mutate({ blocked: false, blocked_reason: null }) : update.mutate({ blocked: true, blocked_reason: blockReason || "Blocked" })}
              className={cn("ml-auto rounded-md px-2 py-1 text-xs font-medium", item.blocked ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400" : "bg-muted text-muted-foreground")}>
              {item.blocked ? "Unblock" : "Mark Blocked"}
            </button>
          </div>
          {!item.blocked && <Input data-testid="block-reason" className="mt-2 h-8 text-xs" placeholder="Reason if marking blocked..." value={blockReason} onChange={(e) => setBlockReason(e.target.value)} />}
          {item.blocked && item.blocked_reason && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{item.blocked_reason}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="mb-2 font-display text-sm font-semibold">Description</h3>
            <p className="text-sm text-foreground">{item.description}</p>
            <h3 className="mb-2 mt-4 font-display text-sm font-semibold">Acceptance Criteria</h3>
            <p className="text-sm text-foreground">{item.acceptance_criteria}</p>
          </div>
          <Checklist list="dor" title="Definition of Ready" />
          <Checklist list="dod" title="Definition of Done" />

          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="mb-2 font-display text-sm font-semibold">Dependencies</h3>
            {(item.blocked_by || []).map((b) => {
              const dep = siblings.find((s) => s.id === b);
              return <div key={b} className="flex items-center gap-2 text-sm text-muted-foreground"><Link2 className="h-3.5 w-3.5 text-amber-500" />Blocked by {dep?.title || b}</div>;
            })}
            {(item.blocked_by || []).length === 0 && <p className="text-sm text-muted-foreground">No dependencies.</p>}
            <div className="mt-3">
              <Select onValueChange={(v) => addDep.mutate(v)}>
                <SelectTrigger className="h-8" data-testid="add-dependency"><SelectValue placeholder="+ Add blocking item" /></SelectTrigger>
                <SelectContent>{siblings.filter((s) => s.id !== itemId && !(item.blocked_by || []).includes(s.id)).map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {(children.length > 0 || ["Epic", "Module", "Task", "Feature"].includes(item.type)) && (
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="mb-2 font-display text-sm font-semibold">Child Tickets</h3>
              {children.length === 0 ? <p className="text-sm text-muted-foreground">No child tickets yet.</p> : (
                <div className="divide-y divide-border">
                  {children.map((c) => (
                    <button key={c.id} onClick={() => navigate(`/pm/projects/${id}/items/${c.id}`)}
                      className="flex w-full items-center gap-2 py-2 text-left hover:bg-muted/50">
                      <StatusBadge family={TYPE_COLOR[c.type]} label={c.type} />
                      <span className="flex-1 text-sm">{c.title}</span>
                      <span className="text-xs text-muted-foreground">{c.story_points} pts</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="mb-3 font-display text-sm font-semibold">Comments</h3>
            <div className="space-y-3">
              {item.comments?.map((c) => (
                <div key={c.id} className="group flex gap-2">
                  <Avatar user={umap[c.author_id]} size="sm" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-[13px]">
                      <span className="font-medium">{umap[c.author_id]?.name}</span>
                      <span className="text-xs text-muted-foreground">{relative(c.created_at)}{c.edited_at ? " (edited)" : ""}</span>
                      {can("pm", "edit") && (
                        <div className="ml-auto hidden items-center gap-0.5 group-hover:flex">
                          <button data-testid={`edit-comment-${c.id}`} onClick={() => { setEditCommentId(c.id); setEditCommentBody(c.body); }}
                            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"><Pencil className="h-3 w-3" /></button>
                          <button data-testid={`del-comment-${c.id}`} onClick={() => deleteComment.mutate(c.id)}
                            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                        </div>
                      )}
                    </div>
                    {editCommentId === c.id ? (
                      <div className="mt-1 flex gap-2">
                        <Textarea value={editCommentBody} onChange={(e) => setEditCommentBody(e.target.value)} rows={2} className="text-sm" />
                        <div className="flex flex-col gap-1">
                          <Button size="sm" onClick={() => editComment.mutate({ cid: c.id, body: { body: editCommentBody } })}>Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditCommentId(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : <p className="text-sm">{c.body}</p>}
                  </div>
                </div>
              ))}
              {(!item.comments || item.comments.length === 0) && <p className="text-sm text-muted-foreground">No comments yet.</p>}
            </div>
            <div className="mt-3 flex gap-2">
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment... use @name to mention someone" rows={1} className="min-h-9" data-testid="comment-input" />
              <Button onClick={() => {
                const mentioned = users.filter((u) => comment.toLowerCase().includes(`@${u.name.split(" ")[0].toLowerCase()}`)).map((u) => u.id);
                addComment.mutate({ author_id: me?.id, body: comment, mentions: mentioned });
              }} disabled={!comment} data-testid="add-comment">Post</Button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <dl className="space-y-3 text-sm">
              <div><dt className="mb-1 text-xs text-muted-foreground">Type</dt><dd>
                <Select value={item.type} onValueChange={(v) => update.mutate({ type: v, is_bug: v === "Bug" })}>
                  <SelectTrigger className="h-8" data-testid="type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TICKET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></dd></div>
              <div><dt className="mb-1 text-xs text-muted-foreground">Parent</dt><dd>
                <Select value={item.parent_id || "none"} onValueChange={(v) => update.mutate({ parent_id: v === "none" ? null : v })}>
                  <SelectTrigger className="h-8" data-testid="parent-select"><SelectValue placeholder="No parent" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No parent</SelectItem>
                    {siblings.filter((s) => s.id !== itemId).map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                  </SelectContent>
                </Select></dd></div>
              <div><dt className="mb-1 text-xs text-muted-foreground">Priority</dt><dd>
                <Select value={item.priority} onValueChange={(v) => update.mutate({ priority: v })}>
                  <SelectTrigger className="h-8" data-testid="priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["low", "medium", "high", "critical"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select></dd></div>
              <div><dt className="mb-1 text-xs text-muted-foreground">Assignee</dt>
                <dd>
                  <Select value={item.assignee_id} onValueChange={(v) => update.mutate({ assignee_id: v, assigned_by: me?.id })}>
                    <SelectTrigger className="h-8" data-testid="assignee-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </dd>
              </div>
              {item.assigned_by && <div className="flex justify-between"><dt className="text-muted-foreground">Assigned By</dt><dd>{umap[item.assigned_by]?.name || "—"}</dd></div>}
              <div><dt className="mb-1 text-xs text-muted-foreground">Expected By</dt><dd>
                <Input type="date" data-testid="due-date-input" className="h-8" value={item.due_date ? item.due_date.slice(0, 10) : ""}
                  onChange={(e) => update.mutate({ due_date: e.target.value ? new Date(e.target.value).toISOString() : null })} /></dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Story Points</dt><dd>
                <Input type="number" className="h-7 w-16 text-right" value={item.story_points} onChange={(e) => update.mutate({ story_points: Number(e.target.value) })} /></dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Est. Hours</dt><dd className="font-mono font-medium">{item.estimated_hours}h</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Logged Hours</dt><dd className="font-mono font-medium">{timeLogged?.total_hours ?? 0}h</dd></div>
              {item.source === "client_request" && <div className="flex justify-between"><dt className="text-muted-foreground">Source</dt><dd><StatusBadge family="violet" label="Client Request" /></dd></div>}
            </dl>
          </div>

          {timeLogged?.entries?.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Time Logged</div>
              <div className="space-y-2 text-xs">
                {timeLogged.entries.map((e, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-border/60 pb-2 last:border-0 last:pb-0">
                    <div className="min-w-0">
                      <div className="font-medium text-foreground">{e.employee_name || "—"}</div>
                      <div className="truncate text-muted-foreground">{e.summary || "—"} · {dateFmt(e.date)}</div>
                    </div>
                    <span className="shrink-0 font-mono font-medium">{e.hours}h</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EpicBurnup({ epicId }) {
  const { data } = useQuery({ queryKey: ["burnup", epicId], queryFn: () => get(`/pm/items/${epicId}/burnup`) });
  if (!data || data.total_items === 0) return null;
  const pct = data.total_hours ? Math.round((data.done_hours / data.total_hours) * 100) : 0;
  return (
    <div className="mb-4 rounded-xl border border-border bg-card p-4" data-testid="epic-burnup">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold">Burn-up (current state)</h3>
        <span className="text-xs text-muted-foreground">{data.done_items}/{data.total_items} items · {data.done_hours}/{data.total_hours}h</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
      <p className="mt-1.5 text-xs text-muted-foreground">A rollup snapshot of all descendant modules/tasks, not a historical time series.</p>
    </div>
  );
}
